import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface FormState {
  name: string;
  email: string;
  phone: string;
  destination: string;
  travelDate: string;
  groupSize: string;
  budget: string;
  message: string;
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    name: '', email: '', phone: '', destination: '',
    travelDate: '', groupSize: '', budget: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError('Message must not exceed 500 characters.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d70vp6muimmp87kg18h0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setForm({ name: '', email: '', phone: '', destination: '', travelDate: '', groupSize: '', budget: '', message: '' });
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Unable to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: 'ri-map-pin-2-line', label: 'Office Address', value: 'Level 5, Tower A, Menara Arus, No. 88, Jalan Ampang, 50450 Kuala Lumpur, Malaysia' },
    { icon: 'ri-phone-line', label: 'Phone', value: '017 645 9709' },
    { icon: 'ri-mail-line', label: 'Email', value: 'info@arustravel.com.my' },
    { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '+6012-345 6789' },
    { icon: 'ri-time-line', label: 'Business Hours', value: 'Monday – Saturday: 9:00am – 6:00pm\nSunday & Public Holidays: Closed' },
  ];

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="relative h-72 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/f629a8811193089200cdf5e8216b8c63.jpg"
          alt="Contact Us"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-emerald-900/72"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pt-20">
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-widest mb-3">We Are Here For You</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-playfair">Contact Us</h1>
          <p className="text-white/75 mt-3 text-base max-w-xl">
            Have a question or ready to start planning? Our travel consultants are here to help.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-gray-900 font-playfair mb-6">Get In Touch</h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Whether you're planning your first international trip or your twentieth adventure, our experienced consultants are ready to help you craft the perfect itinerary.
              </p>
              <div className="space-y-5">
                {contactInfo.map((info) => (
                  <div key={info.label} className="flex gap-4 items-start">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                      <i className={`${info.icon} text-emerald-700 text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{info.label}</p>
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{info.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-gray-500 text-sm mb-4">Follow Us</p>
                <div className="flex gap-3">
                  {[
                    { icon: 'ri-facebook-fill', color: 'hover:bg-emerald-600' },
                    { icon: 'ri-instagram-line', color: 'hover:bg-emerald-600' },
                    { icon: 'ri-whatsapp-line', color: 'hover:bg-emerald-600' },
                    { icon: 'ri-tiktok-fill', color: 'hover:bg-emerald-600' },
                  ].map(({ icon, color }) => (
                    <a
                      key={icon}
                      href="#"
                      rel="nofollow"
                      className={`w-10 h-10 flex items-center justify-center bg-gray-100 ${color} rounded-lg transition-colors cursor-pointer text-gray-600 hover:text-white`}
                    >
                      <i className={`${icon} text-base`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 font-playfair mb-2">Send Us an Enquiry</h2>
                <p className="text-gray-500 text-sm mb-7">Fill in the details below and our team will get back to you within 24 hours.</p>

                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
                      <i className="ri-check-line text-3xl text-emerald-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 font-playfair mb-2">Thank You!</h3>
                    <p className="text-gray-500 text-sm max-w-sm mx-auto">
                      Your enquiry has been received. Our travel consultant will contact you within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-6 text-emerald-700 text-sm font-medium hover:underline cursor-pointer"
                    >
                      Submit another enquiry
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="e.g. Ahmad bin Razak"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number <span className="text-red-400">*</span></label>
                        <input
                          type="tel"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          required
                          placeholder="+601X-XXX XXXX"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Destination</label>
                        <input
                          type="text"
                          name="destination"
                          value={form.destination}
                          onChange={handleChange}
                          placeholder="e.g. Japan, Bali, Europe"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Travel Date</label>
                        <input
                          type="date"
                          name="travelDate"
                          value={form.travelDate}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Group Size</label>
                        <select
                          name="groupSize"
                          value={form.groupSize}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors cursor-pointer"
                        >
                          <option value="">Select</option>
                          <option>1 Person</option>
                          <option>2 Persons</option>
                          <option>3–5 Persons</option>
                          <option>6–10 Persons</option>
                          <option>11–20 Persons</option>
                          <option>20+ Persons</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Budget (per pax)</label>
                        <select
                          name="budget"
                          value={form.budget}
                          onChange={handleChange}
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors cursor-pointer"
                        >
                          <option value="">Select</option>
                          <option>Below RM 2,000</option>
                          <option>RM 2,000 – RM 3,500</option>
                          <option>RM 3,500 – RM 5,000</option>
                          <option>RM 5,000 – RM 8,000</option>
                          <option>Above RM 8,000</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Message
                        <span className="text-gray-400 font-normal ml-2">({form.message.length}/500)</span>
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        maxLength={500}
                        rows={4}
                        placeholder="Tell us about your travel plans, special requests, or any questions you have..."
                        className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-emerald-400 transition-colors resize-none"
                      />
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-md">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-300 text-white font-semibold py-3.5 rounded-md transition-colors text-base cursor-pointer whitespace-nowrap"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-send-plane-line"></i>Send Enquiry
                        </span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-80 w-full">
        <iframe
          title="Arus Travel & Tours Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.7956!2d101.7050!3d3.1570!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc36a45be59dc7%3A0x5adf77f3c15e0!2sJalan%20Ampang%2C%20Kuala%20Lumpur!5e0!3m2!1sen!2smy!4v1700000000000!5m2!1sen!2smy"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>

      <Footer />
    </div>
  );
}
